import type { AtomProperties, BondProperties, DrawingDocument, NormalizedPoint } from '../model';
import { nextSequentialId, replaceTopology, roundCoordinate } from './drawingDocumentCore.ts';

/**
 * #569: Cut/Copy/Paste. A fragment carries atom identity (element, formal charge, isotope,
 * radical electron count), bond identity (order, type, dative donor) and position.
 * #689: it also carries stereochemistry -- tetrahedral and double-bond descriptors, and the bond
 * styles that draw it (solid / hashed wedges, crossed double bonds, a fusion double bond's side)
 * -- remapped to the pasted atom/bond ids, so "Add to paper" of a stereo molecule (and plain
 * copy/paste) keeps its configuration. Paste only translates the fragment (no rotation or mirror),
 * so every winding and wedge stays valid. An entry that references anything outside the copied
 * atoms is dropped (it can't be satisfied). Still not carried: custom atom labels and bond-length
 * overrides, which a pasted copy regains from its re-depiction.
 */
export interface ClipboardBond {
  readonly source: string;
  readonly target: string;
  readonly properties: BondProperties;
}

type Stereo = DrawingDocument['structure']['stereochemistry'];
type TetrahedralStereo = Stereo['tetrahedral'][string];
type DoubleBondStereo = Stereo['doubleBonds'][string];
type BondStyle = DrawingDocument['depiction']['bondStyles'][string];

export interface ClipboardFragment {
  readonly atoms: Readonly<Record<string, AtomProperties>>;
  readonly formalCharges: Readonly<Record<string, number>>;
  readonly positions: Readonly<Record<string, NormalizedPoint>>;
  /** #707 -- the source drawing's coordinate size, which `positions` are normalized to. Pasting
   *  into a drawing of another shape (a tall molecule's depiction onto the wide paper) keeps the
   *  fragment's own proportions and bond lengths; without it, the target's size is assumed. */
  readonly size?: { readonly width: number; readonly height: number };
  readonly bonds: Readonly<Record<string, ClipboardBond>>;
  readonly depths?: Readonly<Record<string, number>>;
  // #689 -- keyed by the copied atom / bond ids, like the maps above.
  readonly tetrahedral?: Readonly<Record<string, TetrahedralStereo>>;
  readonly doubleBonds?: Readonly<Record<string, DoubleBondStereo>>;
  readonly bondStyles?: Readonly<Record<string, BondStyle>>;
  /** #868 -- per-atom / per-bond appearance of the copied atoms and bonds. */
  readonly cycleFills?: DrawingDocument['depiction']['cycleFills'];
  readonly atomAppearance?: DrawingDocument['depiction']['atomAppearance'];
  readonly bondAppearance?: DrawingDocument['depiction']['bondAppearance'];
  /** #736 step 2 -- groups drawn as their labels, whole within the copy. */
  readonly contractedGroups?: DrawingDocument['depiction']['contractedGroups'];
}

/** The atom ids a bond style names (a wedge's narrow end, a double bond's facing side). */
function styleAtomIds(style: BondStyle): string[] {
  if ('fromAtomId' in style) return [style.fromAtomId];
  if ('facingAtomId' in style) return [style.facingAtomId];
  return [];
}

function remapStyle(style: BondStyle, atomIdMap: ReadonlyMap<string, string | undefined>): BondStyle {
  if ('fromAtomId' in style) return { ...style, fromAtomId: atomIdMap.get(style.fromAtomId)! };
  if ('facingAtomId' in style) return { ...style, facingAtomId: atomIdMap.get(style.facingAtomId)! };
  return style;
}

/** Every atom in `atomIds` that still exists, plus every bond internal to that set (both
 *  endpoints included) -- a bond crossing the selection boundary is dropped, not partially
 *  copied. Returns null for an empty/nonexistent selection; there is nothing to put on a
 *  clipboard. */
export function copyFragment(document: DrawingDocument, atomIds: readonly string[]): ClipboardFragment | null {
  const coordinateSet = document.depiction.coordinateSet;
  const includedAtomIds = new Set(atomIds.filter((id) => document.structure.atoms[id]));
  if (includedAtomIds.size === 0) return null;

  const atoms: Record<string, AtomProperties> = {};
  const formalCharges: Record<string, number> = {};
  const positions: Record<string, NormalizedPoint> = {};
  for (const id of includedAtomIds) {
    atoms[id] = document.structure.atoms[id]!;
    formalCharges[id] = document.structure.formalCharges[id]!;
    const coordinate = coordinateSet.coordinates[id]!;
    positions[id] = { x: coordinate.x / coordinateSet.width, y: coordinate.y / coordinateSet.height };
  }

  const bonds: Record<string, ClipboardBond> = {};
  for (const edge of document.structure.graph.edges) {
    // ADR 0026: a hapto bond is copied only with its metal and every ligand atom.
    const ligand = document.structure.bonds[edge.id]?.ligandAtomIds ?? [];
    if (
      includedAtomIds.has(edge.source) &&
      includedAtomIds.has(edge.target) &&
      ligand.every((id) => includedAtomIds.has(id))
    ) {
      bonds[edge.id] = { source: edge.source, target: edge.target, properties: document.structure.bonds[edge.id]! };
    }
  }

  // #689 -- the stereo that only references copied atoms / bonds.
  const hasAtom = (id: string) => includedAtomIds.has(id);
  const tetrahedral = Object.fromEntries(
    Object.entries(document.structure.stereochemistry.tetrahedral).filter(
      ([atomId, stereo]) =>
        hasAtom(atomId) && stereo.ligands.every((ligand) => !('atomId' in ligand) || hasAtom(ligand.atomId))
    )
  );
  const doubleBonds = Object.fromEntries(
    Object.entries(document.structure.stereochemistry.doubleBonds).filter(
      ([bondId, stereo]) => bonds[bondId] && stereo.referenceAtomIds.every(hasAtom)
    )
  );
  const bondStyles = Object.fromEntries(
    Object.entries(document.depiction.bondStyles).filter(
      ([bondId, style]) => bonds[bondId] && styleAtomIds(style).every(hasAtom)
    )
  );
  const atomAppearance = Object.fromEntries(
    Object.entries(document.depiction.atomAppearance ?? {}).filter(([atomId]) => hasAtom(atomId))
  );
  const bondAppearance = Object.fromEntries(
    Object.entries(document.depiction.bondAppearance ?? {}).filter(([bondId]) => bonds[bondId])
  );
  const contractedGroups = Object.fromEntries(
    Object.entries(document.depiction.contractedGroups ?? {}).filter(
      ([attachmentId, entry]) => hasAtom(attachmentId) && entry.atomIds.every(hasAtom)
    )
  );

  return {
    atoms,
    formalCharges,
    positions,
    size: { width: coordinateSet.width, height: coordinateSet.height },
    bonds,
    ...(Object.keys(tetrahedral).length ? { tetrahedral } : {}),
    ...(Object.keys(doubleBonds).length ? { doubleBonds } : {}),
    ...(Object.keys(bondStyles).length ? { bondStyles } : {}),
    ...(document.depiction.cycleFills
      ? { cycleFills: document.depiction.cycleFills.filter((fill) => fill.atomIds.every(hasAtom)) }
      : {}),
    ...(Object.keys(atomAppearance).length ? { atomAppearance } : {}),
    ...(Object.keys(bondAppearance).length ? { bondAppearance } : {}),
    ...(Object.keys(contractedGroups).length ? { contractedGroups } : {}),
    ...(coordinateSet.depths
      ? {
          depths: Object.fromEntries(
            [...includedAtomIds].map((id) => [id, (coordinateSet.depths?.[id] ?? 0) / coordinateSet.width])
          )
        }
      : {})
  };
}

/** How much one unit of the fragment's normalized x / y is in the target's normalized x / y. */
function fragmentScale(fragment: ClipboardFragment, target: { readonly width: number; readonly height: number }) {
  return fragment.size
    ? { x: fragment.size.width / target.width, y: fragment.size.height / target.height }
    : { x: 1, y: 1 };
}

function freshIds(count: number, existingIds: Iterable<string>, prefix: string): string[] {
  const taken = [...existingIds];
  const fresh: string[] = [];
  for (let index = 0; index < count; index += 1) {
    const id = nextSequentialId(taken, prefix);
    taken.push(id);
    fresh.push(id);
  }
  return fresh;
}

export interface PasteResult {
  readonly document: DrawingDocument;
  readonly pastedAtomIds: readonly string[];
  readonly pastedBondIds: readonly string[];
}

/** Inserts `fragment` as a new, disconnected piece of the drawing -- fresh atom/bond ids, so it
 *  can never collide with the existing graph, with its bounding-box center at `target`
 *  (normalized document coordinates). Keep the fragment rigid, even across page edges. Skips
 *  reconcileValenceCharges: the pasted atoms' own valence was already fine when copied, and they
 *  share no bond with anything already on the paper, so nothing existing can become over-valent
 *  either (the same reasoning addAtom uses for a freshly placed lone atom). */
export function pasteFragment(
  document: DrawingDocument,
  fragment: ClipboardFragment,
  target: NormalizedPoint
): PasteResult {
  const points = Object.values(fragment.positions);
  if (!points.length || !Number.isFinite(target.x) || !Number.isFinite(target.y)) {
    return { document, pastedAtomIds: [], pastedBondIds: [] };
  }
  const center = {
    x: (Math.min(...points.map((p) => p.x)) + Math.max(...points.map((p) => p.x))) / 2,
    y: (Math.min(...points.map((p) => p.y)) + Math.max(...points.map((p) => p.y))) / 2
  };
  const oldAtomIds = Object.keys(fragment.atoms);
  const oldBondIds = Object.keys(fragment.bonds);
  const newAtomIds = freshIds(
    oldAtomIds.length,
    document.structure.graph.nodes.map((node) => node.id),
    'n'
  );
  const newBondIds = freshIds(
    oldBondIds.length,
    document.structure.graph.edges.map((edge) => edge.id),
    'e'
  );
  const atomIdMap = new Map(oldAtomIds.map((oldId, index) => [oldId, newAtomIds[index]]));
  const bondIdMap = new Map(oldBondIds.map((oldId, index) => [oldId, newBondIds[index]]));

  const coordinateSet = document.depiction.coordinateSet;
  const scale = fragmentScale(fragment, coordinateSet);
  const atoms = { ...document.structure.atoms };
  const formalCharges = { ...document.structure.formalCharges };
  const coordinates = { ...coordinateSet.coordinates };
  for (const oldId of oldAtomIds) {
    const newId = atomIdMap.get(oldId)!;
    atoms[newId] = fragment.atoms[oldId]!;
    formalCharges[newId] = fragment.formalCharges[oldId]!;
    const position = fragment.positions[oldId]!;
    coordinates[newId] = {
      x: roundCoordinate(((position.x - center.x) * scale.x + target.x) * coordinateSet.width),
      y: roundCoordinate(((position.y - center.y) * scale.y + target.y) * coordinateSet.height)
    };
  }

  const bonds = { ...document.structure.bonds };
  const newEdges = oldBondIds.map((oldId) => {
    const bond = fragment.bonds[oldId]!;
    const newId = bondIdMap.get(oldId)!;
    const properties = bond.properties;
    bonds[newId] = {
      ...properties,
      ...(properties.donorAtomId
        ? { donorAtomId: atomIdMap.get(properties.donorAtomId) ?? properties.donorAtomId }
        : {}),
      // ADR 0026: a hapto bond's ligand is renamed with its atoms.
      ...(properties.ligandAtomIds
        ? {
            ligandAtomIds: properties.ligandAtomIds.map((id) => atomIdMap.get(id) ?? id) as [
              string,
              string,
              ...string[]
            ]
          }
        : {})
    };
    return { id: newId, source: atomIdMap.get(bond.source)!, target: atomIdMap.get(bond.target)! };
  });

  const graph = {
    ...document.structure.graph,
    revision: document.structure.graph.revision + 1,
    nodes: [...document.structure.graph.nodes, ...newAtomIds.map((id) => ({ id }))],
    edges: [...document.structure.graph.edges, ...newEdges]
  };

  const pasted = replaceTopology(
    document,
    graph,
    atoms,
    bonds,
    formalCharges,
    coordinates,
    document.depiction.atomLabels,
    document.depiction.bondLengthOverrides
  );

  // #689 -- the fragment's stereo, remapped onto the pasted ids (replaceTopology above kept the
  // existing drawing's own entries).
  const mapAtom = (id: string) => atomIdMap.get(id)!;
  const withStereo: DrawingDocument = {
    ...pasted,
    structure: {
      ...pasted.structure,
      stereochemistry: {
        tetrahedral: {
          ...pasted.structure.stereochemistry.tetrahedral,
          ...Object.fromEntries(
            Object.entries(fragment.tetrahedral ?? {}).map(([atomId, stereo]) => [
              mapAtom(atomId),
              {
                ...stereo,
                ligands: stereo.ligands.map((ligand) =>
                  'atomId' in ligand ? { atomId: mapAtom(ligand.atomId) } : ligand
                ) as unknown as TetrahedralStereo['ligands']
              }
            ])
          )
        },
        doubleBonds: {
          ...pasted.structure.stereochemistry.doubleBonds,
          ...Object.fromEntries(
            Object.entries(fragment.doubleBonds ?? {}).map(([bondId, stereo]) => [
              bondIdMap.get(bondId)!,
              {
                ...stereo,
                referenceAtomIds: stereo.referenceAtomIds.map(
                  mapAtom
                ) as unknown as DoubleBondStereo['referenceAtomIds']
              }
            ])
          )
        }
      }
    },
    depiction: {
      ...pasted.depiction,
      ...(fragment.cycleFills?.length || pasted.depiction.cycleFills?.length
        ? {
            cycleFills: [
              ...(pasted.depiction.cycleFills ?? []),
              ...(fragment.cycleFills ?? []).map((fill) => ({
                ...fill,
                atomIds: fill.atomIds.map((id) => atomIdMap.get(id)!) as [string, string, string, ...string[]]
              }))
            ]
          }
        : {}),
      bondStyles: {
        ...pasted.depiction.bondStyles,
        ...Object.fromEntries(
          Object.entries(fragment.bondStyles ?? {}).map(([bondId, style]) => [
            bondIdMap.get(bondId)!,
            remapStyle(style, atomIdMap)
          ])
        )
      },
      // #736 step 2 -- groups drawn as their labels, on the pasted ids.
      ...(fragment.contractedGroups || pasted.depiction.contractedGroups
        ? {
            contractedGroups: {
              ...pasted.depiction.contractedGroups,
              ...Object.fromEntries(
                Object.entries(fragment.contractedGroups ?? {}).map(([attachmentId, entry]) => [
                  atomIdMap.get(attachmentId)!,
                  {
                    ...entry,
                    atomIds: entry.atomIds.map((id) => atomIdMap.get(id)!) as unknown as typeof entry.atomIds
                  }
                ])
              )
            }
          }
        : {}),
      // #868 -- the copied appearance, on the pasted ids.
      ...(fragment.atomAppearance || pasted.depiction.atomAppearance
        ? {
            atomAppearance: {
              ...pasted.depiction.atomAppearance,
              ...Object.fromEntries(
                Object.entries(fragment.atomAppearance ?? {}).map(([atomId, value]) => [atomIdMap.get(atomId)!, value])
              )
            }
          }
        : {}),
      ...(fragment.bondAppearance || pasted.depiction.bondAppearance
        ? {
            bondAppearance: {
              ...pasted.depiction.bondAppearance,
              ...Object.fromEntries(
                Object.entries(fragment.bondAppearance ?? {}).map(([bondId, value]) => [bondIdMap.get(bondId)!, value])
              )
            }
          }
        : {})
    }
  };

  const withDepths = fragment.depths
    ? {
        ...withStereo,
        depiction: {
          ...withStereo.depiction,
          coordinateSet: {
            ...withStereo.depiction.coordinateSet,
            depths: {
              ...withStereo.depiction.coordinateSet.depths,
              ...Object.fromEntries(
                oldAtomIds.map((id) => [atomIdMap.get(id)!, (fragment.depths?.[id] ?? 0) * coordinateSet.width])
              )
            }
          }
        }
      }
    : withStereo;
  return { document: withDepths, pastedAtomIds: newAtomIds, pastedBondIds: newBondIds };
}

/** Gap between an added fragment and what is already drawn (normalized document units). */
const ALONGSIDE_GAP = 0.06;

/**
 * #689 -- where "Add to paper" puts a new fragment: to the right of everything already drawn
 * (vertically centred on it) when that still fits the page, otherwise below it; the page centre
 * when the paper is empty. Returns the fragment's bounding-box centre, as pasteFragment expects.
 */
/**
 * #707 -- makes room for just-pasted atoms: a structure kept at its own size (a long chain from
 * "Add to paper") can reach past the drawing's edges. Those atoms alone are nudged in from the
 * left / top edge, and the coordinate space grows right / down to hold them, so nothing already
 * drawn moves (and what is tied to its coordinates -- reaction boxes, Markush layers -- stays put).
 * Returns the same document when everything already fits.
 */
export function containPastedAtoms(
  document: DrawingDocument,
  atomIds: readonly string[],
  padding: number
): DrawingDocument {
  const coordinateSet = document.depiction.coordinateSet;
  const points = atomIds.map((id) => coordinateSet.coordinates[id]).filter((point) => point !== undefined);
  if (points.length === 0) return document;
  const shiftX = Math.max(0, padding - Math.min(...points.map((point) => point.x)));
  const shiftY = Math.max(0, padding - Math.min(...points.map((point) => point.y)));
  const width = Math.max(coordinateSet.width, Math.max(...points.map((point) => point.x)) + shiftX + padding);
  const height = Math.max(coordinateSet.height, Math.max(...points.map((point) => point.y)) + shiftY + padding);
  if (shiftX === 0 && shiftY === 0 && width === coordinateSet.width && height === coordinateSet.height) return document;
  const coordinates = { ...coordinateSet.coordinates };
  for (const id of atomIds) {
    const point = coordinates[id];
    if (point) coordinates[id] = { x: roundCoordinate(point.x + shiftX), y: roundCoordinate(point.y + shiftY) };
  }
  return {
    ...document,
    depiction: {
      ...document.depiction,
      coordinateSet: { ...coordinateSet, coordinates, width: Math.ceil(width), height: Math.ceil(height) }
    }
  };
}

export function placeAlongside(document: DrawingDocument, fragment: ClipboardFragment): NormalizedPoint {
  const { coordinates, width, height } = document.depiction.coordinateSet;
  const existing = document.structure.graph.nodes
    .map((node) => coordinates[node.id])
    .filter((point): point is NonNullable<typeof point> => point !== undefined)
    .map((point) => ({ x: point.x / width, y: point.y / height }));
  if (existing.length === 0) return { x: 0.5, y: 0.5 };

  const span = (values: readonly number[]) => [Math.min(...values), Math.max(...values)] as const;
  const [minX, maxX] = span(existing.map((p) => p.x));
  const [minY, maxY] = span(existing.map((p) => p.y));
  const points = Object.values(fragment.positions);
  const [fragmentMinX, fragmentMaxX] = span(points.map((p) => p.x));
  const [fragmentMinY, fragmentMaxY] = span(points.map((p) => p.y));
  const scale = fragmentScale(fragment, { width, height });
  const fragmentWidth = (fragmentMaxX - fragmentMinX) * scale.x;
  const fragmentHeight = (fragmentMaxY - fragmentMinY) * scale.y;

  const right = { x: maxX + ALONGSIDE_GAP + fragmentWidth / 2, y: (minY + maxY) / 2 };
  if (right.x + fragmentWidth / 2 <= 1 - ALONGSIDE_GAP / 2) return right;
  return { x: (minX + maxX) / 2, y: maxY + ALONGSIDE_GAP + fragmentHeight / 2 };
}
